/**
 * Dashboard Sidebar Component
 *
 * Collapsible sidebar navigation for dashboard pages
 * Features:
 * - Auto-collapse on smaller viewports (< 1200px)
 * - Hover to expand when collapsed
 * - Pin button to keep expanded
 * - Mobile drawer mode
 * - Grouped navigation with icons
 */

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { withTimeout } from "../../utils/withTimeout";
import { toolRegistry } from "../../tools";
import {
  FaTh,
  FaCube,
  FaChevronDown,
  FaTimes,
  FaThumbtack,
  FaMapMarkedAlt,
  FaUsers,
  FaDatabase,
  FaCog,
} from "react-icons/fa";
import "./DashboardSidebar.scss";
import { lighten } from "../../utils/toolTheme";

const DashboardSidebar = ({ mobileOpen, onMobileClose, owner }) => {
  const location = useLocation();
  const { isAdmin, userProfile } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState([
    "main",
    "tools",
    "system",
  ]);
  const [visibleTools, setVisibleTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPinned, setIsPinned] = useState(() => {
    // Remember pin preference
    const saved = localStorage.getItem("dashboardSidebarPinned");
    return saved ? JSON.parse(saved) : true;
  });
  const [isHovering, setIsHovering] = useState(false);

  // Save pin preference
  useEffect(() => {
    localStorage.setItem("dashboardSidebarPinned", JSON.stringify(isPinned));
  }, [isPinned]);

  // Load visible tools based on global permissions and user preferences
  useEffect(() => {
    let cancelled = false;

    // Admins see all tools regardless of global permissions (getVisibleTools
    // ignores globalPermissions for admins — only their hiddenTools matters,
    // already in userProfile). Skip the settings/toolPermissions read entirely:
    // synchronous, instant, and can't hang on a stale connection or lose an
    // async race and drop the menu to non-admin tools.
    if (isAdmin) {
      setVisibleTools(
        toolRegistry.getVisibleTools(
          { isAdmin: true },
          {},
          userProfile?.hiddenTools || [],
          userProfile?.shownTools || [],
        ),
      );
      setLoading(false);
      return;
    }

    async function loadVisibleTools() {
      try {
        // Timeout-guard the permissions read so an unstable connection can't
        // wedge it. On failure we fall back to the accessible-tools set below.
        const settingsDoc = await withTimeout(
          getDoc(doc(db, "settings", "toolPermissions")),
          8000,
        );
        if (cancelled) return;
        const globalPermissions = settingsDoc.exists()
          ? settingsDoc.data().tools || {}
          : {};
        const hiddenTools = userProfile?.hiddenTools || [];

        setVisibleTools(
          toolRegistry.getVisibleTools(
            { isAdmin },
            globalPermissions,
            hiddenTools,
            userProfile?.shownTools || [],
          ),
        );
      } catch (error) {
        console.error("Error loading visible tools:", error);
        if (!cancelled)
          setVisibleTools(toolRegistry.getAccessibleTools({ isAdmin }, userProfile?.shownTools || []));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadVisibleTools();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, userProfile]);

  // Determine if sidebar should be collapsed
  // Collapses when unpinned AND not hovering (regardless of screen size)
  const shouldCollapse = !isPinned && !isHovering;

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const isActive = (path) => location.pathname === path;

  const isItemActive = (item) =>
    item.exact ? isActive(item.path) : location.pathname.startsWith(item.path);

  /**
   * Tint a nav icon with its tool's identity colour.
   *
   * ⚠️ The sidebar is nearly black (#1d1d1b), so raw accents — rose #be123c,
   * slate #475569 — are close to invisible on it. Each is lightened toward
   * white before use.
   *
   * The ACTIVE item shows its colour at full strength; the others sit back at
   * partial opacity, and only while the menu is actually open. Colouring every
   * icon equally would make the palette compete with the selection itself,
   * which is the one thing the sidebar has to communicate.
   */
  const menuOpen = !shouldCollapse; // pinned open, or hovered open
  const iconTint = (item, active) => {
    if (!item.color) return undefined;
    const tint = lighten(item.color, 0.42);
    if (active) return { color: tint, opacity: 1 };
    return menuOpen ? { color: tint, opacity: 0.55 } : undefined;
  };

  const navGroups = [
    {
      id: "main",
      label: "Main",
      items: [
        {
          path: "/dashboard",
          label: "Dashboard",
          icon: FaTh,
          exact: true,
        },
      ],
    },
    {
      id: "tools",
      label: "Tools",
      items: [
        {
          path: "/dashboard/map",
          label: "Map",
          icon: FaMapMarkedAlt,
          exact: true,
        },
        ...visibleTools
          // ⚠️ PERMISSION and PLACEMENT are different questions, and this used to
          // conflate them: requiresAdmin forced a tool into System, so anything
          // admin-only was also demoted out of the working nav. System Health is
          // admin-only because it exposes cost and infrastructure — but it is a
          // thing you CONSULT, especially when something is wrong, so burying it
          // under System is exactly backwards. `mainNav` promotes a tool without
          // widening who can see it; the requiresAdmin gate is untouched, and
          // visibleTools has already applied it.
          .filter((tool) => !tool.requiresAdmin || tool.mainNav)
          .map((tool) => ({
            path: tool.path,
            label: tool.name,
            icon: tool.icon,
            // Identity colour, deliberately NOT controlColor — the sidebar is
            // where you recognise a tool, not where you operate it.
            color: tool.color,
          })),
      ],
    },
    {
      id: "system",
      label: "System",
      items: [
        ...(isAdmin
          ? [
              // Admin tools from the registry. They are filtered OUT of the
              // Tools group above, so without this they appeared nowhere at
              // all and were reachable only by typing the URL. Paths already
              // listed by hand are skipped so nothing double-renders.
              ...visibleTools
                .filter(
                  (tool) =>
                    tool.requiresAdmin &&
                    !tool.mainNav && // promoted to the Tools group above
                    ![
                      "/dashboard/config",
                      "/dashboard/backup",
                      "/dashboard/users",
                      "/dashboard/settings",
                    ].includes(tool.path),
                )
                .map((tool) => ({
                  path: tool.path,
                  label: tool.name,
                  icon: tool.icon,
                  color: tool.color,
                })),
              {
                path: "/dashboard/config",
                label: "Blackbox Config",
                icon: FaCube,
              },
              {
                path: "/dashboard/backup",
                label: "Backup System",
                icon: FaDatabase,
              },
              {
                path: "/dashboard/users",
                label: "User Management",
                icon: FaUsers,
              },
              {
                path: "/dashboard/settings",
                label: "System Settings",
                icon: FaCog,
              },
            ]
          : []),
      ],
    },
  ];

  // Filter groups based on admin status and remove empty groups
  const filteredNavGroups = navGroups
    .filter((group) => !group.adminOnly || isAdmin)
    .filter((group) => group.items.length > 0); // Hide groups with no items

  return (
    <aside
      className={`dashboard-sidebar ${shouldCollapse ? "collapsed" : ""} ${
        isPinned ? "pinned" : ""
      } ${mobileOpen ? "mobile-open" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Mobile close button */}
      <button className="mobile-close-btn" onClick={onMobileClose}>
        <FaTimes />
      </button>

      {/* Pin icon - shows when not in mobile mode */}
      {!mobileOpen && (
        <FaThumbtack
          className={`pin-icon ${isPinned ? "pinned" : ""}`}
          onClick={() => setIsPinned(!isPinned)}
          aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
          title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
        />
      )}

      {/* Navigation */}
      <nav className="sidebar-nav">
        {filteredNavGroups.map((group) => (
          <div key={group.id} className="nav-group">
            <button
              className={`group-header ${
                expandedGroups.includes(group.id) ? "expanded" : ""
              }`}
              onClick={() => toggleGroup(group.id)}
            >
              <span className="group-label">{group.label}</span>
              <FaChevronDown className="group-chevron" />
            </button>

            <ul
              className={`group-items ${
                !expandedGroups.includes(group.id) ? "hidden" : ""
              }`}
            >
              {group.items.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item ${isItemActive(item) ? "active" : ""}`}
                    onClick={onMobileClose}
                    title={
                      shouldCollapse && !isHovering ? item.label : undefined
                    }
                  >
                    <item.icon
                      className="nav-icon"
                      style={iconTint(item, isItemActive(item))}
                    />
                    <span className="nav-label">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default DashboardSidebar;
